import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Truck, Building, Loader2, MapPin } from "lucide-react";
import { toast } from "react-toastify";
import {
  getOrderDetails,
  setDeliveryMethod as setDeliveryMethodApi,
  DeliveryMethodData,
} from "core/Services/MerchantServices";
import { getCities, getCityAreas } from "core/Services/AdminServices";
import { iOrderDetails } from "core/kangarooTypes/iOrderDetails";
import Button from "shared/Button";

interface City {
  id: number;
  name: string;
  price: number;
}

interface Area {
  id: number;
  name: string;
  price: number;
}

const DeliveryMethodForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const merchantId = new URLSearchParams(location.search).get("merchantId");

  const [order, setOrder] = useState<iOrderDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [deliveryMethod, setDeliveryMethod] = useState<"roots" | "third_party">(
    "roots"
  );
  const [cities, setCities] = useState<City[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [selectedCity, setSelectedCity] = useState<number | null>(null);
  const [selectedArea, setSelectedArea] = useState<number | null>(null);

  const [isLoadingAreas, setIsLoadingAreas] = useState(false);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  useEffect(() => {
    if (selectedCity) {
      loadAreas(selectedCity);
    } else {
      setAreas([]);
      setSelectedArea(null);
    }
  }, [selectedCity]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [orderResponse, citiesResponse] = await Promise.all([
        getOrderDetails(id!),
        getCities(),
      ]);

      setOrder(orderResponse.data);
      setCities(citiesResponse.data || []);

      // Pre-select if order already has city/area
      if (orderResponse.data.customer?.city) {
        const city = citiesResponse.data?.find(
          (c: City) => c.name === orderResponse.data.customer.city
        );
        if (city) {
          setSelectedCity(city.id);
        }
      }
    } catch (error) {
      toast.error("Failed to load order details");
    } finally {
      setIsLoading(false);
    }
  };

  const loadAreas = async (cityId: number) => {
    try {
      setIsLoadingAreas(true);
      const response = await getCityAreas(cityId.toString());
      setAreas(response.data || []);
    } catch (error) {
      setAreas([]);
    } finally {
      setIsLoadingAreas(false);
    }
  };

  const handleSave = async () => {
    if (!order) return;

    if (deliveryMethod === "roots" && !selectedCity) {
      toast.error("Please select a city for Roots delivery");
      return;
    }

    setIsSaving(true);
    try {
      const data: DeliveryMethodData = {
        delivery_method: deliveryMethod,
      };

      if (deliveryMethod === "roots") {
        data.city_id = selectedCity!;
        if (selectedArea) {
          data.area_id = selectedArea;
        }
      }

      await setDeliveryMethodApi(order.id, data);
      toast.success(
        deliveryMethod === "roots"
          ? "Order is now shipped"
          : "Order handed off to third party"
      );
      const detailsUrl = `/order-details/:id=${
        order.id
      }?pageTitle=Order Details${
        merchantId ? `&merchantId=${merchantId}` : ""
      }`;
      navigate(detailsUrl);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to set delivery method"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const getSelectedCityPrice = (): number => {
    if (!selectedCity) return 0;
    const city = cities.find((c) => c.id === selectedCity);
    return city?.price ? Number(city.price) : 0;
  };

  const getSelectedAreaPrice = (): number => {
    if (!selectedArea) return 0;
    const area = areas.find((a) => a.id === selectedArea);
    return area?.price ? Number(area.price) : 0;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">Order not found</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">
          Delivery Method - Order #{order.external_order_number || order.id}
        </h1>
        <span className="px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
          {order.status?.replace(/_/g, " ")}
        </span>
      </div>

      {/* Order Summary */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h3 className="font-medium mb-2">Order Summary</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Items:</span>{" "}
            <span className="font-medium">{order.items?.length || 0}</span>
          </div>
          <div>
            <span className="text-gray-500">Price:</span>{" "}
            <span className="font-medium">
              {Number(order.price || 0).toFixed(3)} JOD
            </span>
          </div>
          <div>
            <span className="text-gray-500">Pick & Pack:</span>{" "}
            <span className="font-medium">
              {Number(order.pick_pack_fee || 0).toFixed(3)} JOD
            </span>
          </div>
          <div>
            <span className="text-gray-500">Services:</span>{" "}
            <span className="font-medium">
              {Number(order.services_fee || 0).toFixed(3)} JOD
            </span>
          </div>
        </div>
      </div>

      {/* Delivery Method Selection */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Select Delivery Method</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* By Roots */}
          <button
            onClick={() => setDeliveryMethod("roots")}
            className={`p-4 border-2 rounded-lg text-left transition-colors ${
              deliveryMethod === "roots"
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200 hover:border-blue-300"
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <Truck
                className={`w-6 h-6 ${
                  deliveryMethod === "roots" ? "text-blue-600" : "text-gray-400"
                }`}
              />
              <span className="font-semibold">Deliver by Roots</span>
            </div>
            <p className="text-sm text-gray-500">
              Ship via Roots delivery network. Select city and optionally area.
            </p>
          </button>

          {/* Third Party */}
          <button
            onClick={() => setDeliveryMethod("third_party")}
            className={`p-4 border-2 rounded-lg text-left transition-colors ${
              deliveryMethod === "third_party"
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200 hover:border-blue-300"
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <Building
                className={`w-6 h-6 ${
                  deliveryMethod === "third_party"
                    ? "text-blue-600"
                    : "text-gray-400"
                }`}
              />
              <span className="font-semibold">Third Party Delivery</span>
            </div>
            <p className="text-sm text-gray-500">
              Hand off to external carrier. Order will be marked as handed off.
            </p>
          </button>
        </div>
      </div>

      {/* Roots Delivery Options */}
      {deliveryMethod === "roots" && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Delivery Location
          </h2>

          <div className="space-y-4">
            {/* City Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={selectedCity || ""}
                onChange={(e) =>
                  setSelectedCity(Number(e.target.value) || null)
                }
              >
                <option value="">Select a city...</option>
                {cities.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name} ({city.price.toFixed(3)} JOD)
                  </option>
                ))}
              </select>
            </div>

            {/* Area Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Area (Optional)
              </label>
              {isLoadingAreas ? (
                <div className="flex items-center gap-2 text-gray-500 p-3">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading areas...
                </div>
              ) : (
                <select
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={selectedArea || ""}
                  onChange={(e) =>
                    setSelectedArea(Number(e.target.value) || null)
                  }
                  disabled={!selectedCity || areas.length === 0}
                >
                  <option value="">
                    {areas.length === 0
                      ? "No areas available"
                      : "Select an area (optional)..."}
                  </option>
                  {areas.map((area) => (
                    <option key={area.id} value={area.id}>
                      {area.name} ({area.price.toFixed(3)} JOD)
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Delivery Fee Preview */}
            {selectedCity && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Estimated Delivery Fee:</p>
                <p className="text-xl font-bold text-blue-600">
                  {(selectedArea
                    ? getSelectedAreaPrice()
                    : getSelectedCityPrice()
                  ).toFixed(3)}{" "}
                  JOD
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {selectedArea
                    ? "Based on selected area"
                    : "Based on selected city"}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Warning for partial shipping */}
      {order.has_missing_items && deliveryMethod === "roots" && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-yellow-800 text-sm">
            <strong>Note:</strong> This order has missing items. It will be
            marked as "Ship Partially" instead of "Shipped".
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-4 justify-end">
        <Button
          variant="secondary"
          onClick={() =>
            navigate(
              `/order-details/:id=${order.id}?pageTitle=Order Details${
                merchantId ? `&merchantId=${merchantId}` : ""
              }`
            )
          }
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaving || (deliveryMethod === "roots" && !selectedCity)}
        >
          {isSaving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Saving...
            </>
          ) : deliveryMethod === "roots" ? (
            "Ship Order"
          ) : (
            "Hand Off to Third Party"
          )}
        </Button>
      </div>
    </div>
  );
};

export default DeliveryMethodForm;
